import { useState } from "react";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { EP, Btn, inputCls } from "./employeePortalParts";

export default function EmployeePortalLogin({ onAuthed, magicError }) {
  const [identity, setIdentity] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("identity");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(magicError || null);

  const start = async () => {
    if (identity.trim().length < 3) { setErr("Enter your work email or mobile number"); return; }
    setBusy(true); setErr(null);
    try {
      const { data } = await EP.post("/auth/otp/start", { identity: identity.trim() });
      setStage("code");
      setMsg(data.delivery === "unavailable" ? data.hint : `Code sent${data.channels?.length ? ` via ${data.channels.join(" & ")}` : ""}. It expires in 5 minutes.`);
    } catch (e) { setErr(e.response?.data?.detail || "Could not send a code"); }
    setBusy(false);
  };
  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code"); return; }
    setBusy(true); setErr(null);
    try { await EP.post("/auth/otp/verify", { identity: identity.trim(), code }); onAuthed(); }
    catch (e) { setErr(e.response?.data?.detail || "Invalid code"); }
    setBusy(false);
  };

  return (
    <div className="min-h-[100dvh] bg-[#F4F6FA] flex items-start sm:items-center justify-center px-4 py-10" data-testid="employee-login">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <img src="/suntronix-logo.webp?v=color1" alt="Suntronix" className="h-8 w-auto" />
          <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Secure</span>
        </div>
        <h1 className="text-2xl font-bold text-[#0B1730]">Employee Portal</h1>
        <p className="text-[14px] text-slate-500 mt-1 mb-6">Your equipment, deliveries, support and returns — in one place. No password needed.</p>
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 space-y-4">
          {stage === "identity" ? (
            <>
              <label className="block text-[12px] font-semibold text-slate-600">Work email or mobile number</label>
              <input className={inputCls} value={identity} onChange={(e) => setIdentity(e.target.value)} placeholder="you@company.com or 98xxxxxxxx" autoComplete="username" inputMode="email" data-testid="ep-identity" onKeyDown={(e) => e.key === "Enter" && start()} />
              <Btn onClick={start} disabled={busy} testid="ep-send-code">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Send me a code</Btn>
            </>
          ) : (
            <>
              <p className="text-[13px] text-slate-600" data-testid="ep-code-msg">{msg}</p>
              <input className={`${inputCls} text-center tracking-[0.4em] text-xl font-mono`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" inputMode="numeric" autoComplete="one-time-code" data-testid="ep-code" onKeyDown={(e) => e.key === "Enter" && verify()} />
              <Btn onClick={verify} disabled={busy} testid="ep-verify">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in</Btn>
              <button className="text-[12px] text-slate-500 underline w-full" onClick={() => { setStage("identity"); setCode(""); setMsg(null); }} data-testid="ep-back">Use a different email / number</button>
            </>
          )}
          {err && <p className="text-[13px] text-rose-600" role="alert" data-testid="ep-error">{err}</p>}
        </div>
        <p className="text-[11px] text-slate-400 mt-4 text-center">Got a sign-in link from IT? Just open it — it signs you in automatically.</p>
      </div>
    </div>
  );
}
